import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, User, Phone, Mail, MessageSquare, Send } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const tabData = {
  School: {
    title: "School Trips",
    photos: [
      "https://images.unsplash.com/photo-1511629091441-ee46146481b6?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1577896851231-70ef18881754?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
    ],
    whyUs: "Safe, educational, and fun-filled adventures designed specifically for students. We prioritize safety and learning while ensuring kids have the time of their lives.",
    whatWeProvide: [
      "Dedicated tour guides and chaperones",
      "Educational itineraries tailored to curriculum",
      "Safe and comfortable transportation",
      "Kid-friendly meals and accommodations",
      "24/7 on-ground support"
    ]
  },
  College: {
    title: "College Trips",
    photos: [
      "https://images.unsplash.com/photo-1523580494863-6f3031224c94?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1528605248644-14dd04022da1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1539635278303-d4002c07eae3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
    ],
    whyUs: "Thrilling and budget-friendly trips that create lifelong memories with friends. Perfect balance of adventure, relaxation, and party.",
    whatWeProvide: [
      "Budget-friendly group packages",
      "Adventure activities (trekking, rafting, etc.)",
      "Flexible itineraries",
      "Accommodation in vibrant locations",
      "Campfires and group event arrangements"
    ]
  },
  Corporate: {
    title: "Corporate Outings",
    photos: [
      "https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1517048676732-d65bc937f952?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
    ],
    whyUs: "Professional, seamless, and engaging team-building retreats. We handle all logistics so you can focus on bonding and rejuvenating your team.",
    whatWeProvide: [
      "Premium resort bookings",
      "Customized team-building activities",
      "Conference and meeting room setups",
      "Gala dinners and entertainment",
      "End-to-end event management"
    ]
  }
};

type Tab = 'School' | 'College' | 'Corporate';

const GroupTrip = () => {
  const [activeTab, setActiveTab] = useState<Tab>('School');
  const [checkIn, setCheckIn] = useState<Date | null>(null);
  const [checkOut, setCheckOut] = useState<Date | null>(null);

  const activeContent = tabData[activeTab];

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-4 mb-12">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight"
          >
            Unforgettable <span className="text-green-600">Group Trips</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600 max-w-2xl mx-auto"
          >
            Whether it's learning, bonding, or just having fun together, we craft the perfect itinerary for your group.
          </motion.p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-12">
          <div className="bg-white p-1.5 rounded-full shadow-sm border border-gray-100 flex space-x-1">
            {(Object.keys(tabData) as Tab[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`relative px-6 py-2.5 text-sm font-medium rounded-full transition-colors ${
                  activeTab === tab ? 'text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {activeTab === tab && (
                  <motion.div
                    layoutId="active-tab"
                    className="absolute inset-0 bg-green-500 rounded-full"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
                <span className="relative z-10">{tab}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100 mb-16"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Image Grid */}
              <div className="p-8 pb-0 lg:pb-8 h-full">
                <div className="grid grid-cols-2 gap-4 h-full min-h-[400px]">
                  <div className="col-span-2 h-64 md:h-full relative rounded-2xl overflow-hidden shadow-sm">
                    <img src={activeContent.photos[0]} alt={`${activeTab} trip`} className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="h-48 relative rounded-2xl overflow-hidden shadow-sm">
                    <img src={activeContent.photos[1]} alt={`${activeTab} group`} className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="h-48 relative rounded-2xl overflow-hidden shadow-sm">
                    <img src={activeContent.photos[2]} alt={`${activeTab} activity`} className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  </div>
                </div>
              </div>

              {/* Brief Content */}
              <div className="p-8 pt-4 lg:pt-8 flex flex-col justify-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">{activeContent.title}</h2>
                
                <div className="space-y-8">
                  <div>
                    <h3 className="text-xl font-semibold text-green-600 mb-3 flex items-center gap-2">
                      <span className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">?</span>
                      Why Us?
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-lg">
                      {activeContent.whyUs}
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-green-600 mb-3 flex items-center gap-2">
                      <span className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">✓</span>
                      What We Provide
                    </h3>
                    <ul className="space-y-3">
                      {activeContent.whatWeProvide.map((item, index) => (
                        <li key={index} className="flex items-start gap-3 text-gray-600">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2.5 flex-shrink-0" />
                          <span className="text-lg">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Enquiry Form */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-12 relative overflow-hidden">
            {/* Decorative background element */}
            <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 rounded-full bg-green-50 opacity-50 blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-64 h-64 rounded-full bg-blue-50 opacity-50 blur-3xl pointer-events-none" />
            
            <div className="relative z-10">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-3">Plan Your Group Trip</h2>
                <p className="text-gray-600">Fill out the form below and our group travel experts will get in touch with you shortly.</p>
              </div>

              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 ml-1">Full Name</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        className="block w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                        placeholder="John Doe"
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 ml-1">Phone Number</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Phone className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="tel"
                        className="block w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                        placeholder="+91 98765 43210"
                      />
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 ml-1">Email Address</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      className="block w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-20">
                  {/* Check-in */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 ml-1">Expected Check-in</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                        <Calendar className="h-5 w-5 text-gray-400" />
                      </div>
                      <DatePicker
                        selected={checkIn}
                        onChange={(date) => setCheckIn(date)}
                        className="block w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                        placeholderText="Select date"
                        wrapperClassName="w-full"
                      />
                    </div>
                  </div>

                  {/* Check-out */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 ml-1">Expected Check-out</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                        <Calendar className="h-5 w-5 text-gray-400" />
                      </div>
                      <DatePicker
                        selected={checkOut}
                        onChange={(date) => setCheckOut(date)}
                        className="block w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                        placeholderText="Select date"
                        minDate={checkIn || undefined}
                        wrapperClassName="w-full"
                      />
                    </div>
                  </div>
                </div>

                {/* Special Request */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 ml-1">Special Requests (Optional)</label>
                  <div className="relative">
                    <div className="absolute top-3 left-0 pl-4 flex items-start pointer-events-none">
                      <MessageSquare className="h-5 w-5 text-gray-400" />
                    </div>
                    <textarea
                      rows={4}
                      className="block w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all resize-none"
                      placeholder="Any specific requirements, group size, destinations in mind..."
                    />
                  </div>
                </div>

                <button
                  type="button"
                  className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-4 px-8 rounded-xl transition-colors duration-200 shadow-lg shadow-green-200 mt-8"
                >
                  <span>Send Enquiry</span>
                  <Send className="w-5 h-5" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupTrip;
